import { useEffect, memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { StarIcon } from '@heroicons/react/24/solid'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import { trackEvent } from '../../utils/analytics'

const AdminEntitiesSection = memo(function AdminEntitiesSection({ onBack }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: entities,
    loading,
    error,
    searchInput,
    search,
    filter,
    pagination,
    handleSearchChange,
    handleFilterChange,
    handlePageChange,
    refetch
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        // Hook provides params with: { page, limit, search?, filter? }
        const apiParams = { ...params }
        
        // Map filter to status
        if (params.filter && params.filter !== 'all') {
          apiParams.status = params.filter
          delete apiParams.filter
        }
        
        const response = await adminAPI.getAllEntities(apiParams)
        return {
          data: response.data?.data || [],
          pagination: {
            page: response.data?.pagination?.page || 1,
            totalPages: response.data?.pagination?.totalPages || 1,
            total: response.data?.pagination?.total || 0
          }
        }
      })
    },
    { pageSize: 20 }
  )

  const filterOptions = [
    { value: 'all', label: 'All Entities' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'verified', label: 'Verified' },
    { value: 'unverified', label: 'Unverified' }
  ]

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(new Set(entities.map(e => e.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [entities])

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleBulkVerify = useCallback(async () => {
    if (selectedIds.size === 0) return

    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => adminAPI.verifyEntity(id)))
      setSelectedIds(new Set())
      refetch()
    }, { successMessage: `${selectedIds.size} entity(ies) verified successfully` })
  }, [selectedIds, refetch, execute])

  const handleToggleFeatured = useCallback(async (entityId, currentlyFeatured) => {
    if (currentlyFeatured) {
      // Remove featured status
      if (!confirm('Remove featured status from this entity?')) return
      
      await execute(async () => {
        await adminAPI.unsetFeaturedEntity()
        trackEvent('Admin', 'unset_featured_entity', entityId)
        refetch()
      }, { successMessage: 'Featured entity removed' })
    } else {
      // Set as featured
      if (!confirm('Set this entity as the featured entity? This will replace any existing featured entity.')) return
      
      await execute(async () => {
        await adminAPI.setFeaturedEntity(entityId)
        trackEvent('Admin', 'set_featured_entity', entityId)
        refetch()
      }, { successMessage: 'Entity is now featured!' })
    }
  }, [execute, refetch])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Entities Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/entities/new')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm font-medium"
          >
            + Create Entity
          </button>
          <button
            onClick={onBack}
            className="text-sm text-primary hover:text-primary-600"
          >
            ← Back to Overview
          </button>
        </div>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search by name, address, or category..."
        onSearchChange={handleSearchChange}
        filterValue={filter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="entities"
        onPageChange={handlePageChange}
      />

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="px-4 lg:px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.size} entity(ies) selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkVerify}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              Verify Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={entities.length > 0 && selectedIds.size === entities.length}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
              </th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Featured</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">{t('app.loading')}</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-red-500">{error}</td>
              </tr>
            ) : entities.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 lg:px-6 py-8 text-center text-gray-500">No entities found</td>
              </tr>
            ) : (
              entities.map((entity) => (
                <tr 
                  key={entity.id}
                  onClick={() => navigate(`/admin/entities/${entity.id}`)}
                  className={`cursor-pointer hover:bg-gray-50 ${selectedIds.has(entity.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(entity.id)}
                      onChange={() => handleSelectOne(entity.id)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 lg:px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{entity.name}</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{entity.entityType || entity.type}</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{entity.category}</div>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      entity.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {entity.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {entity.isVerified && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Verified
                      </span>
                    )}
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggleFeatured(entity.id, entity.isFeatured)}
                      disabled={loading}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        entity.isFeatured
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={entity.isFeatured ? 'Remove featured status' : 'Set as featured entity'}
                    >
                      <StarIcon className={`h-4 w-4 mr-1 ${entity.isFeatured ? 'text-yellow-500' : 'text-gray-400'}`} />
                      {entity.isFeatured ? 'Featured' : 'Set Featured'}
                    </button>
                  </td>
                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(entity.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export default AdminEntitiesSection
